import { ReactNode } from 'react';
import {
  Modal,
  ModalContent,
  ModalOverlay,
  ModalBody,
  VStack,
  Text,
  FormLabel,
  FormControl,
  FormErrorMessage,
  Input,
  Divider,
  Icon,
  HStack,
  Center,
  InputRightAddon,
  InputGroup,
  Spinner,
  InputRightElement,
  Button,
} from '@chakra-ui/react';
import { IoMailOpenOutline } from 'react-icons/io5';
import * as fp from 'lodash/fp';
import { EnterpriseGroupBuy, PaymentTypeString } from '~/types/enterpriseGroupBuy';
import { AiOutlineMessage } from 'react-icons/ai';
import { BsPersonFill } from 'react-icons/bs';
import { WiTime3 } from 'react-icons/wi';
import { ImLocation2 } from 'react-icons/im';
import { IoCashOutline } from 'react-icons/io5';
import { RiTimerLine } from 'react-icons/ri';

import { format } from 'date-fns';
import { zhTW } from 'date-fns/locale';
import { IconType } from 'react-icons';
import { InputVariant } from '~/theme/components/Input';
import useLogin, { ConfirmStatus } from '../LoginModal/hooks/useLogin';
import { useLocation } from 'react-router-dom';

const ROWS: Array<{
  id: number;
  icon: IconType;
  label: ReactNode;
  formatFn?: (d: EnterpriseGroupBuy) => ReactNode;
  key: keyof EnterpriseGroupBuy;
}> = [
  {
    id: 1,
    key: 'pickMealAt',
    icon: WiTime3,
    label: '取餐時間',
    formatFn: (d: EnterpriseGroupBuy) =>
      format(d.pickMealAt.toDate(), 'M/d(eee) HH:mm', { locale: zhTW }) +
      format(d.pickMealEnd.toDate(), '-HH:mm', { locale: zhTW }),
  },
  {
    id: 2,
    key: 'addressSpec',
    icon: ImLocation2,
    label: '取餐地點',
  },
  {
    id: 3,
    key: 'paymentType',
    icon: IoCashOutline,
    label: '付款方式',
    formatFn: (d) => (d.paymentType ? PaymentTypeString?.[d?.paymentType] : '-'),
  },
  {
    id: 4,
    key: 'orderEndAt',
    icon: RiTimerLine,
    label: '收單時間',
    formatFn: (d) => format(d.orderEndAt.toDate(), 'M/d(eee) HH:mm', { locale: zhTW }),
  },
];

type LoginModalProps = {
  isOpen: boolean;
  onClose: () => void;
  enterpriseGroupBuyInfo: EnterpriseGroupBuy;
};

const Row = ({ label, value, icon }: { label: ReactNode; value: ReactNode; icon: IconType }) => {
  return (
    <HStack
      w="full"
      align="center"
      justify="space-between"
      fontSize="14px"
      fontWeight={400}
      color="#333"
    >
      <HStack flex={1} spacing="8px" align="center" justify="flex-start">
        {<Icon as={icon} fontSize="20px" />}
        <Text>{label}</Text>
      </HStack>
      <Text fontWeight={700}>{value}</Text>
    </HStack>
  );
};

export default function LoginModal({ isOpen, onClose, enterpriseGroupBuyInfo }: LoginModalProps) {
  const location = useLocation();
  const {
    isVerifying,
    confirmStatus,
    state: { form, error },
    handleChangeState,
    handleLogin,
  } = useLogin();

  const handleLineLogin = () => {
    const clientId = process.env.REACT_APP_LINE_CLIENT_ID || '';
    const platformId = process.env.REACT_APP_PLATFORM_ID || '';
    const host = process.env.REACT_APP_BACKEND_URL || '';
    const callbackUrl =
      // @ts-ignore
      `${window.location.pathname}+${location.state?.previous}`;

    const redirectUri = `${host}/httpLineLogin/redirect/${platformId}?callbackUrl=${callbackUrl}`;
    const u = new URL('https://access.line.me/oauth2/v2.1/authorize');
    u.searchParams.append('response_type', 'code');
    u.searchParams.append('client_id', clientId);
    u.searchParams.append('redirect_uri', redirectUri);
    u.searchParams.append('state', Math.random().toString(32).substring(2));
    u.searchParams.append('scope', 'profile openid');

    window.location.href = u.toString();
  };

  return (
    <Modal size="sm" isOpen={isOpen} onClose={onClose} isCentered closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalBody overflowY="auto" p="8px 24px">
          <VStack gap="12px">
            <Center h="83px" flexDir="column" sx={{ gap: '12px' }}>
              <Icon
                alignSelf="center"
                as={IoMailOpenOutline}
                borderRadius="full"
                w="49px"
                h="49px"
                bgColor="#FFF9EF"
                color="secondary"
                p={1}
              />
              <Text fontSize="md" fontWeight="bold" textAlign="center">
                歡迎參加{enterpriseGroupBuyInfo?.name || '-'}的團購
              </Text>
            </Center>

            <Divider />

            {ROWS.map((row, index) => {
              return (
                <Row
                  key={`${row.key}-${index}`}
                  icon={row.icon}
                  label={row.label}
                  value={
                    row.formatFn && enterpriseGroupBuyInfo
                      ? row.formatFn(enterpriseGroupBuyInfo)
                      : enterpriseGroupBuyInfo?.[row?.key]
                  }
                />
              );
            })}

            <Divider />

            <Row icon={BsPersonFill} label="主辦人" value={enterpriseGroupBuyInfo?.sponsorName} />

            {enterpriseGroupBuyInfo?.sponsorTalk ? (
              <>
                <Row icon={AiOutlineMessage} label="主辦人的話" value="" />
                <Text
                  p="5px 8px"
                  borderRadius="5px"
                  w="100%"
                  bg="#F9F9F9"
                  fontSize="12px"
                  fontWeight={400}
                >
                  {enterpriseGroupBuyInfo?.sponsorTalk || ''}
                </Text>
              </>
            ) : null}

            <Divider />

            <Text fontSize="14px" fontWeight={700} textAlign="center">
              登入參加團購
            </Text>
            <FormControl isRequired isInvalid={!!error.name}>
              <FormLabel fontSize="sm" fontWeight="normal">
                姓名
              </FormLabel>
              <Input
                variant={InputVariant.OUTLINE_DARK}
                value={form.name}
                onChange={(e) => handleChangeState.changeName(e.target.value)}
                type="text"
                inputMode="text"
                isDisabled={fp.eq(confirmStatus, ConfirmStatus.CONFIRMING)}
              />
              <FormErrorMessage bgColor="danger.200">{error.name}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={!!error.phoneNumber}>
              <FormLabel fontSize="sm" fontWeight="normal">
                手機
              </FormLabel>
              <InputGroup>
                <Input
                  variant={InputVariant.OUTLINE_DARK}
                  value={form.phoneNumber}
                  onChange={(e) => handleChangeState.changePhoneNumber(e.target.value)}
                  type="tel"
                  inputMode="tel"
                  isDisabled={fp.eq(confirmStatus, ConfirmStatus.CONFIRMING)}
                />

                <InputRightAddon
                  as="button"
                  cursor="pointer"
                  minW="7rem"
                  justifyContent="center"
                  bgColor={fp.eq(confirmStatus, ConfirmStatus.CONFIRMING) ? 'gray.200' : 'dark'}
                  onClick={fp.eq(confirmStatus, ConfirmStatus.CONFIRMING) ? () => {} : handleLogin}
                >
                  {fp.eq(confirmStatus, ConfirmStatus.CONFIRMING) ? (
                    <Spinner />
                  ) : (
                    <Text color="white">傳遞驗證碼</Text>
                  )}
                </InputRightAddon>
              </InputGroup>
              <FormErrorMessage bgColor="danger.200">{error.phoneNumber}</FormErrorMessage>
            </FormControl>

            {fp.eq(confirmStatus, ConfirmStatus.CONFIRMED) && (
              <FormControl isRequired isInvalid={!!error.verifyCode}>
                <FormLabel fontSize="sm" fontWeight="normal">
                  驗證碼
                </FormLabel>
                <InputGroup>
                  <Input
                    variant={InputVariant.OUTLINE_DARK}
                    value={form.verifyCode}
                    onChange={(e) => handleChangeState.changeVerifyCode(e.target.value)}
                    type="number"
                    inputMode="numeric"
                    isDisabled={isVerifying}
                  />
                  <InputRightElement>{isVerifying && <Spinner />}</InputRightElement>
                </InputGroup>
                <FormErrorMessage bgColor="danger.200">{error.verifyCode}</FormErrorMessage>
              </FormControl>
            )}

            {process.env.REACT_APP_LINE_CLIENT_ID?.length ? (
              <VStack w="80%" mt="12px" spacing="10px" pb="16px">
                <HStack w="full" spacing="29px" width="100%">
                  <HStack borderBottom="1px solid #C4C4C4" flex={1} />
                  <Text>或</Text>
                  <HStack borderBottom="1px solid #C4C4C4" flex={1} />
                </HStack>
                <Button bgColor="#01B901" _hover={{ bgColor: '#01B901' }} onClick={handleLineLogin}>
                  使用LINE帳號登入
                </Button>
              </VStack>
            ) : null}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
